import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
  useNodes,
  useEdges,
} from "@xyflow/react";
import {
  GitBranch,
  Trash2,
  Settings,
  X,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Plus,
} from "lucide-react";
import syntaxHighlight from "../../utils/jsonViewer";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";
import { AvailableVariable, ConditionOperator } from "../../common/ui/types";
import { formatDragVariable } from "../../utils/variableHelper";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import NodeDocsButton from "../../docs/NodeDocsButton";

const OPERATORS = [
  { value: ConditionOperator.EQUALS, label: "Equals" },
  { value: ConditionOperator.NOT_EQUALS, label: "Not Equals" },
  { value: ConditionOperator.GREATER_THAN, label: "Greater Than" },
  { value: ConditionOperator.LESS_THAN, label: "Less Than" },
  {
    value: ConditionOperator.GREATER_THAN_OR_EQUAL,
    label: "Greater Than or Equal",
  },
  { value: ConditionOperator.LESS_THAN_OR_EQUAL, label: "Less Than or Equal" },
  { value: ConditionOperator.CONTAINS, label: "Contains" },
  { value: ConditionOperator.NOT_CONTAINS, label: "Does Not Contain" },
  { value: ConditionOperator.STARTS_WITH, label: "Starts With" },
  {
    value: ConditionOperator.DOES_NOT_START_WITH,
    label: "Does Not Start With",
  },
  { value: ConditionOperator.ENDS_WITH, label: "Ends With" },
  { value: ConditionOperator.DOES_NOT_END_WITH, label: "Does Not End With" },
  { value: ConditionOperator.MATCHES_REGEX, label: "Matches Regex" },
  {
    value: ConditionOperator.DOES_NOT_MATCH_REGEX,
    label: "Does Not Match Regex",
  },
  { value: ConditionOperator.IS_EMPTY, label: "Is Empty" },
  { value: ConditionOperator.IS_NOT_EMPTY, label: "Is Not Empty" },
  { value: ConditionOperator.EXISTS, label: "Exists" },
  { value: ConditionOperator.DOES_NOT_EXIST, label: "Does Not Exist" },
];

interface Condition {
  key: string;
  operator: ConditionOperator;
  value: string;
}

export const IfElseNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  // Initialize conditions from data or default
  const initialConditions: Condition[] = data?.conditions ||
    data?.inputs?.conditions || [
      { key: "", operator: ConditionOperator.EQUALS, value: "" },
    ];
  const initialLogicalOperator =
    data?.logical_operator || data?.inputs?.logical_operator || "AND";

  const [conditions, setConditions] = useState<Condition[]>(initialConditions);
  const [logicalOperator, setLogicalOperator] = useState<"AND" | "OR">(
    initialLogicalOperator,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setDraggedVariable] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState<{
    index: number;
    field: "key" | "value";
  } | null>(null);

  const { setNodes, deleteElements } = useReactFlow();
  const nodes = useNodes();
  const edges = useEdges();

  // Derived condition string for display
  const conditionDisplay = useMemo(() => {
    if (conditions.length === 0) return "";
    return conditions
      .map((c) => {
        if (!c.key && !c.value) return "";
        return `(${c.key} ${c.operator} ${c.value})`;
      })
      .filter(Boolean)
      .join(` ${logicalOperator} `);
  }, [conditions, logicalOperator]);

  // Get execution status from node data
  const executionStatus = data?.executionStatus || data?.status || null;
  const isExecuting =
    executionStatus === "running" || executionStatus === "IN_PROGRESS";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => {
      e.stopPropagation();
    },
    [],
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeleteNode();
  };

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const extractNodeData = useCallback((nodeData: any) => {
    if (!nodeData || typeof nodeData !== "object") {
      return { inputs: {}, outputs: {} };
    }

    const outputs = nodeData.outputs || {};
    const inputs: Record<string, any> = {};
    Object.entries(nodeData).forEach(([key, value]) => {
      if (key !== "outputs") inputs[key] = value;
    });

    return { inputs, outputs };
  }, []);

  const previousNodes = useMemo(() => {
    const ancestorNodes = getAllAncestorNodes(id, nodes, edges);

    return ancestorNodes.map((sourceNode) => {
      const { inputs, outputs } = extractNodeData(sourceNode.data);
      return {
        nodeId: sourceNode.id,
        nodeName: sourceNode.data?.name || sourceNode.id,
        nodeType: sourceNode.type || "unknown",
        inputs,
        outputs,
      };
    }) as AvailableVariable[];
  }, [id, nodes, edges, extractNodeData]);

  const handleDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output",
  ) => {
    e.stopPropagation();
    const fullVariable = formatDragVariable(nodeName, variable, type);
    setDraggedVariable(fullVariable);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", fullVariable);
  };

  const handleDrop = (
    e: React.DragEvent,
    index: number,
    field: "key" | "value",
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");

    if (variable) {
      const newConditions = [...conditions];
      newConditions[index] = { ...newConditions[index], [field]: variable };
      setConditions(newConditions);
    }
    setDraggedVariable(null);
    setIsDraggingOver(null);
  };

  const handleDragOver = (
    e: React.DragEvent,
    index: number,
    field: "key" | "value",
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver({ index, field });
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(null);
  };

  const addCondition = () => {
    setConditions([
      ...conditions,
      { key: "", operator: ConditionOperator.EQUALS, value: "" },
    ]);
  };

  const removeCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    setConditions(
      newConditions.length
        ? newConditions
        : [{ key: "", operator: ConditionOperator.EQUALS, value: "" }],
    );
  };

  const updateCondition = (
    index: number,
    field: keyof Condition,
    value: string,
  ) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setConditions(newConditions);
  };

  const statusConfig = useMemo(() => {
    if (isExecuting) {
      return { bg: "bg-blue-500", text: "Executing...", pulse: true };
    } else if (executionSuccess) {
      return {
        bg: "bg-green-500",
        text: "Executed Successfully",
        pulse: false,
      };
    } else if (executionError) {
      return { bg: "bg-red-500", text: "Execution Failed", pulse: false };
    }

    const hasConfig = conditions.some((c) => c.key && c.operator);
    return hasConfig
      ? { bg: "bg-emerald-500", text: "Configured", pulse: false }
      : { bg: "bg-gray-400", text: "Not Configured", pulse: false };
  }, [conditions, isExecuting, executionSuccess, executionError]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,

              conditions,
              logical_operator: logicalOperator,
              condition: conditionDisplay, // Keep for backward compatibility or display
            },
          };
        }
        return node;
      }),
    );
  }, [conditions, logicalOperator, id, setNodes, conditionDisplay]);

  const hasOutputs = data.outputs && Object.keys(data.outputs).length > 0;

  return (
    <div
      className={`relative group bg-gradient-to-br from-violet-50 to-fuchsia-50 border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
        isExecuting
          ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
          : executionSuccess
            ? "border-green-500 shadow-md shadow-green-200"
            : executionError
              ? "border-red-500 shadow-md shadow-red-200"
              : selected
                ? "border-violet-500 shadow-md"
                : "border-violet-300 hover:border-violet-300"
      }`}
    >
      {/* Execution Overlay Spinner */}
      {isExecuting && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="bg-white rounded-full p-3 shadow-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}

       {/* Success Overlay */}
            {executionSuccess && (
              <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
                <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              </div>
            )}
      
            {/* Error Overlay */}
            {executionError && (
              <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
                <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
              </div>
            )}

      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-violet-500 border-2 border-white"
        style={{
          boxShadow:
            "0 0 0 4px rgba(139, 92, 246, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
        }}
      />

      <div className="flex items-start justify-between mb-3 cursor-move">
        <div className="flex items-center gap-2.5">
          <div
            className={`p-1.5 rounded-lg shadow-sm transition-all ${
              isExecuting
                ? "bg-blue-100 animate-pulse"
                : executionSuccess
                  ? "bg-green-100"
                  : executionError
                    ? "bg-red-100"
                    : "bg-violet-100"
            }`}
          >
            {isExecuting ? (
              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            ) : (
              <GitBranch
                className={`w-4 h-4 ${
                  executionSuccess
                    ? "text-green-700"
                    : executionError
                      ? "text-red-700"
                      : "text-violet-700"
                }`}
              />
            )}
          </div>
          <div>
            <h3 className="font-medium text-gray-800 text-sm">IF-ELSE</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div
                className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg} ${
                  statusConfig.pulse ? "animate-pulse" : ""
                }`}
              />
              <p className="text-xs text-gray-600">{statusConfig.text}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NodeDocsButton nodeType="IF-ELSE" />
          <button
            onClick={handleDeleteClick}
            className="text-gray-500 hover:text-gray-700 p-1 rounded transition"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mb-3" onClick={stopPropagation}>
        <button
          onClick={handleOpenModal}
          className="w-full py-2.5 rounded-lg bg-violet-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-violet-700 transition-colors"
        >
          <Settings className="w-3.5 h-3.5" /> Configure IF-ELSE
        </button>
      </div>

      {/* Output Handles - Right Side */}
      <div className="absolute -right-[60px] top-1/2 -translate-y-1/2 flex flex-col gap-8">
        {/* True Branch */}
        <div className="relative flex items-center">
          <span className="absolute -top-4 left-2 text-[10px] text-violet-600 font-medium">
            true
          </span>
          <div className="w-[36px] h-0.5 bg-violet-300" />
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            className="!w-6 !h-6 !bg-white !border-2 !border-violet-300 !rounded-md flex items-center justify-center !relative !transform-none !top-auto !right-auto hover:border-violet-500 transition-colors"
          >
            <Plus className="w-3.5 h-3.5 text-violet-600" />
          </Handle>
        </div>

        {/* False Branch */}
        <div className="relative flex items-center">
          <span className="absolute -top-4 left-2 text-[10px] text-violet-600 font-medium">
            false
          </span>
          <div className="w-[36px] h-0.5 bg-violet-300" />
          <Handle
            type="source"
            position={Position.Right}
            id="false"
            className="!w-6 !h-6 !bg-white !border-2 !border-violet-300 !rounded-md flex items-center justify-center !relative !transform-none !top-auto !right-auto hover:border-violet-500 transition-colors"
          >
            <Plus className="w-3.5 h-3.5 text-violet-600" />
          </Handle>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-[95vw] h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-50 rounded">
                    <GitBranch className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure IF-ELSE
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Set condition expression
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Three-column Layout */}
              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* Left Sidebar - Previous Nodes */}
                <div className="w-[340px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleDragStart}
                  />
                </div>

                {/* Center - Main Configuration */}
                <div className="flex-1 overflow-y-auto bg-white p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    IF-ELSE Configuration
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Configure the condition expression
                  </p>

                  <div className="space-y-6">
                    {/* Logical Operator */}
                    {conditions.length > 1 && (
                      <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">
                          Logical Operator:
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setLogicalOperator("AND")}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                              logicalOperator === "AND"
                                ? "bg-violet-600 text-white"
                                : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            AND
                          </button>
                          <button
                            onClick={() => setLogicalOperator("OR")}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                              logicalOperator === "OR"
                                ? "bg-violet-600 text-white"
                                : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            OR
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Conditions List */}
                    <div className="space-y-4">
                      {conditions.map((condition, index) => (
                        <div
                          key={index}
                          className="p-4 border border-gray-200 rounded-lg bg-gray-50/50 hover:border-violet-200 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Condition {index + 1}
                            </span>
                            {conditions.length > 1 && (
                              <button
                                onClick={() => removeCondition(index)}
                                className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-12 gap-3">
                            {/* Left Value */}
                            <div className="col-span-4">
                              <input
                                type="text"
                                value={condition.key}
                                onChange={(e) =>
                                  updateCondition(index, "key", e.target.value)
                                }
                                onDrop={(e) => handleDrop(e, index, "key")}
                                onDragOver={(e) =>
                                  handleDragOver(e, index, "key")
                                }
                                onDragLeave={handleDragLeave}
                                placeholder="Value 1 (e.g. {{file.name}})"
                                className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition font-mono ${
                                  isDraggingOver?.index === index &&
                                  isDraggingOver?.field === "key"
                                    ? "border-violet-400 bg-violet-50 ring-2 ring-violet-200"
                                    : "border-gray-300 hover:border-violet-400"
                                }`}
                              />
                            </div>

                            {/* Operator */}
                            <div className="col-span-4">
                              <div className="relative">
                                <select
                                  value={condition.operator}
                                  onChange={(e) =>
                                    updateCondition(
                                      index,
                                      "operator",
                                      e.target.value as ConditionOperator,
                                    )
                                  }
                                  className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition appearance-none cursor-pointer hover:border-violet-400"
                                >
                                  {OPERATORS.map((op) => (
                                    <option key={op.value} value={op.value}>
                                      {op.label}
                                    </option>
                                  ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                  <svg
                                    className="w-4 h-4 text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>

                            {/* Right Value */}
                            <div className="col-span-4">
                              <input
                                type="text"
                                value={condition.value}
                                onChange={(e) =>
                                  updateCondition(
                                    index,
                                    "value",
                                    e.target.value,
                                  )
                                }
                                onDrop={(e) => handleDrop(e, index, "value")}
                                onDragOver={(e) =>
                                  handleDragOver(e, index, "value")
                                }
                                onDragLeave={handleDragLeave}
                                placeholder="Value 2"
                                disabled={[
                                  ConditionOperator.IS_EMPTY,
                                  ConditionOperator.IS_NOT_EMPTY,
                                  ConditionOperator.EXISTS,
                                  ConditionOperator.DOES_NOT_EXIST,
                                ].includes(condition.operator)}
                                className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition font-mono ${
                                  isDraggingOver?.index === index &&
                                  isDraggingOver?.field === "value"
                                    ? "border-violet-400 bg-violet-50 ring-2 ring-violet-200"
                                    : "border-gray-300 hover:border-violet-400"
                                } disabled:bg-gray-100 disabled:text-gray-400`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={addCondition}
                      className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 text-sm font-medium hover:border-violet-400 hover:text-violet-600 hover:bg-violet-50 transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Another Condition
                    </button>
                  </div>
                </div>

                {/* Right Sidebar - Outputs */}
                <div className="w-80 bg-gray-50 overflow-y-auto p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Node Outputs
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Available after execution
                  </p>

                  {hasOutputs && (
                    <div className="mt-4 pt-3 border-t border-violet-200">
                      <h4 className="text-xs font-semibold text-gray-600 mb-2">
                        JSON Output
                      </h4>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                        <pre className="whitespace-pre-wrap break-words">
                          <code
                            dangerouslySetInnerHTML={{
                              __html: syntaxHighlight(data.outputs),
                            }}
                          />
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Expected Output Info */}
                  <div className="mt-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">
                      Expected Outputs
                    </h4>
                    <div className="space-y-2 text-xs text-gray-600">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>TRUE branch: When condition is met</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span>FALSE branch: When condition is not met</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  {conditions.some((c) => c.key && c.operator) ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Configuration Valid</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-yellow-700">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">
                        Please fill required fields
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default IfElseNode;
